import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SingleItemDetailTemplate from '../../../Reusable_Components/SingleItemDetailTemplate';
import { API_ROUTES, API_DOMAIN, headers } from '../../../Reusable_Components/Config/apiConfig';
import { ToastContext } from '../../../../context/ToastContext';
import ConfirmModal from '../../../Reusable_Components/ConfirmModal';

const CustomerNotesDetails = () => {
  // const { noteId } = useParams(); // Get note ID from URL
  const { noteId: id } = useParams(); // Destructure noteId matching the Route parameter
  const decodedId = id ? atob(id) : id;
  const noteId = decodedId ? decodedId.replace(/\D/g, '') : '';
  const [noteDetails, setNoteDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [showDelete, setShowDelete] = useState(false);
  const navigate = useNavigate();
  const { showToast } = useContext(ToastContext);

  // Fetch note details, users, and customers
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('customer_management_token');
        const reqHeaders = {
          ...headers,
          ...(token ? { Authorization: `Token ${token}` } : {}),
        };

        // Fetch customer note details
        const noteResponse = await axios.get(`${API_DOMAIN}${API_ROUTES.customerNote}${noteId}/`, { headers: reqHeaders });
        setNoteDetails(noteResponse.data);

        // Fetch customers
        const customerResponse = await axios.get(`${API_DOMAIN}${API_ROUTES.customer}`, { headers: reqHeaders });
        const custData = Array.isArray(customerResponse.data) ? customerResponse.data : (customerResponse.data.results || []);
        setCustomers(custData);

        // Fetch users
        const userResponse = await axios.get(`${API_DOMAIN}${API_ROUTES.users}`, { headers: reqHeaders });
        const userData = Array.isArray(userResponse.data) ? userResponse.data : (userResponse.data.results || []);
        setUsers(userData);

        setLoading(false);
      } catch (err) {
        console.error('Error fetching customer note details:', err);
        setError('Unable to fetch details.');
        setLoading(false);
      }
    };

    fetchData();
  }, [noteId]);

  // Handle delete
  const handleDelete = async () => {
    try {
      const token = localStorage.getItem('customer_management_token');
      const reqHeaders = {
        ...headers,
        ...(token ? { Authorization: `Token ${token}` } : {}),
      };

      await axios.delete(`${API_DOMAIN}${API_ROUTES.customerNote}${noteId}/`, { headers: reqHeaders });
      showToast('Note deleted successfully', 'success');
      setShowDelete(false);
      navigate('/customer/note/list');
    } catch (err) {
      console.error('Error deleting note:', err);
      showToast('Failed to delete note', 'error');
    }
  };

  // Helper functions to map IDs to names
  const getCustomerName = (cust) => {
    if (cust && typeof cust === 'object') return cust.name;
    const customer = customers.find((customer) => customer.id === cust);
    return customer ? customer.name : 'Unknown Customer';
  };

  const getUserName = (usr) => {
    if (usr && typeof usr === 'object') return usr.username;
    const user = users.find((user) => user.id === usr);
    return user ? user.username : 'Unknown User';
  };

  // Map resolved names to a flat details object
  const displayDetails = {
    customer_name: getCustomerName(noteDetails.customer),
    user_name: getUserName(noteDetails.user),
    text: noteDetails.text,
  };

  // Define columns using fields matching the displayDetails keys
  const columns = [
    { label: 'Customer', field: 'customer_name' },
    { label: 'User', field: 'user_name' },
    { label: 'Note Text', field: 'text' },
  ];

  return (
    <div>
      <h1>Customer Note Details</h1>
      <SingleItemDetailTemplate
        details={displayDetails}
        columns={columns}
        loading={loading}
        entityName="Note"
        listPath="/customer/note/list"
        editPath={`/customer/note/input/${btoa(noteId)}`}
        onDelete={() => setShowDelete(true)}
      />
      <ConfirmModal
        isOpen={showDelete}
        title="Delete Note"
        message="Are you sure you want to delete this note?"
        onConfirm={handleDelete}
        onCancel={() => setShowDelete(false)}
      />
    </div>
  );
};

export default CustomerNotesDetails;
